/* eslint-disable react/no-array-index-key */
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Fab,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Link,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import { ScaleLoader } from 'react-spinners';

import WrapperSimple from '../../../../layout-components/ExampleWrapperSimple';
import UseStyles from '../../../../components/styles/loadingButton';
import { CpfCnpjMask, cpfOrCnpj } from '../../../../components/mask';
import { FINANCIAL_RESPONSIBILITY_TERMS } from '../../../../constants/financialTerms';
import { useAuth } from '../../../../contexts/auth';
import {
  EmailAuthProvider,
  getAuth,
  reauthenticateWithCredential
} from '../../../../config/firebase';
import { FinancialConfig } from '../../../../models/interfaces/subsidiary';
import { Banks, BanksList } from '../../../../models/enums/banks';
import { BanksList as BankServicesList } from '../../../../models/enums/bankService';
import subsidiaryService from '../../../../services/subsidiaryService';
import messageService from '../../../../services/messageService';
import { isValidCpfOrCnpj, removeMask } from '../../../../utils/stringHelper';

type FinancialConfigItem = FinancialConfig & { localId: string };

type ServiceOption = { value: string; label: string };

const bankServiceOptions: ServiceOption[] = Object.entries(BankServicesList).map(
  ([value, label]) => ({ value, label })
);

const createLocalId = () => crypto.randomUUID();

const emptyConfig = (): FinancialConfigItem => ({
  bank: '',
  services: [],
  account: '',
  pixKey: '',
  cpfCnpj: '',
  disabledAt: null,
  acceptedAt: null,
  tax: 0,
  localId: createLocalId()
});

const parseDateValue = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  if (typeof value === 'object' && value !== null && 'seconds' in value) {
    return new Date((value as { seconds: number }).seconds * 1000);
  }
  return null;
};

const toFinancialConfigItem = (config: FinancialConfig): FinancialConfigItem => ({
  bank: config.bank ?? '',
  services: config.services ?? [],
  account: config.account ?? '',
  pixKey: config.pixKey ?? '',
  cpfCnpj: config.cpfCnpj ? cpfOrCnpj(removeMask(config.cpfCnpj)) : '',
  disabledAt: parseDateValue(config.disabledAt),
  acceptedAt: parseDateValue(config.acceptedAt),
  tax: config.tax ?? 0,
  localId: createLocalId()
});

const getCpfCnpjError = (value: string) => {
  if (!value.trim()) return 'CPF ou CNPJ é obrigatório';
  if (!isValidCpfOrCnpj(value)) return 'CPF ou CNPJ inválido';
  return '';
};

const getAccountDuplicateKey = (config: FinancialConfigItem) => {
  const bank = config.bank.trim().toLowerCase();
  const account = removeMask(config.account) ?? '';
  return `${bank}|${account}`;
};

const getPixDuplicateKey = (config: FinancialConfigItem) => config.pixKey.trim().toLowerCase();

const findDuplicateFinancialConfig = (
  items: FinancialConfigItem[]
): { index: number; message: string } | null => {
  const accountKeys = new Map<string, number>();
  const pixKeys = new Map<string, number>();

  for (let index = 0; index < items.length; index += 1) {
    const config = items[index];
    const accountKey = getAccountDuplicateKey(config);
    const pixKey = getPixDuplicateKey(config);

    if (accountKey !== '|' && accountKeys.has(accountKey)) {
      return {
        index,
        message: `Conta duplicada: ${BanksList[config.bank as Banks] ?? config.bank} / ${config.account}`
      };
    }

    if (pixKey && pixKeys.has(pixKey)) {
      return {
        index,
        message: `Chave Pix duplicada: ${config.pixKey}`
      };
    }

    if (accountKey !== '|') accountKeys.set(accountKey, index);
    if (pixKey) pixKeys.set(pixKey, index);
  }

  return null;
};

const toComparableFinancialConfig = (config: FinancialConfig) => ({
  bank: config.bank.trim().toLowerCase(),
  services: [...(config.services ?? [])].map((item) => item.trim()).sort(),
  account: removeMask(config.account) ?? '',
  pixKey: config.pixKey.trim().toLowerCase(),
  cpfCnpj: removeMask(config.cpfCnpj) ?? '',
  disabled: Boolean(config.disabledAt),
  tax: config.tax ?? 0
});

const hasFinancialConfigChanged = (
  current: FinancialConfigItem[],
  previous: FinancialConfig[]
) => {
  const currentComparable = current.map(({ localId, ...config }) => toComparableFinancialConfig(config));
  const previousComparable = previous.map(toComparableFinancialConfig);
  return JSON.stringify(currentComparable) !== JSON.stringify(previousComparable);
};

const getPasswordErrorMessage = (error: any): string => {
  const code = error?.code as string | undefined;
  if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
    return 'Senha incorreta. Tente novamente.';
  }
  if (code === 'auth/too-many-requests') {
    return 'Muitas tentativas. Aguarde um momento e tente novamente.';
  }
  if (error?.message) return error.message;
  return 'Não foi possível confirmar a senha.';
};

const EFI_PARTNER_URL = 'https://sejaefi.com.br/parceiro/juma-entregas';

const EFI_THEME = {
  fontFamily: '"Red Hat Display", "Helvetica Neue", Arial, sans-serif',
  primary: '#00809d',
  primaryDark: '#006177',
  primaryLight: '#20b6d7',
  accent: '#0ba1c2',
  background: 'linear-gradient(135deg, #f0fbfd 0%, #e6f7fa 100%)'
};

export default function FinancialSettings() {
  const classes = UseStyles();
  const { subsidiaryId, user, isReadOnly } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [configs, setConfigs] = useState<FinancialConfigItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [cpfCnpjErrors, setCpfCnpjErrors] = useState<Record<string, string>>({});
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsAcceptedAt, setTermsAcceptedAt] = useState<Date | null>(null);
  const termsAcceptedAtRef = useRef<Date | null>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [savedConfigs, setSavedConfigs] = useState<FinancialConfig[]>([]);
  const [subsidiaryEmail, setSubsidiaryEmail] = useState('');
  const [subsidiaryName, setSubsidiaryName] = useState('');

  if (!subsidiaryId) throw new Error('Usuário sem permissão');

  const buttonClassname = clsx({ [classes.buttonSuccess]: success });
  const selectedConfig = selectedIndex !== null ? configs[selectedIndex] : null;
  const selectedCpfCnpjError = selectedConfig ? cpfCnpjErrors[selectedConfig.localId] : '';
  const isEfipaySelected = selectedConfig?.bank === Banks.Efibank;
  const canSaveAsManager = Boolean(user?.claims?.manager) && !isReadOnly;

  const clearTermsAcceptance = () => {
    setTermsAccepted(false);
    setTermsAcceptedAt(null);
    termsAcceptedAtRef.current = null;
  };

  const assertFranchiseManagerPermission = async () => {
    if (!user?.uid) {
      throw new Error('Usuário não autenticado');
    }
    if (!user.claims?.manager) {
      throw new Error('Somente o gerente da franquia pode salvar esta configuração');
    }
    if (isReadOnly) {
      throw new Error('Usuário com permissão somente leitura não pode salvar esta configuração');
    }

    const subsidiary = await subsidiaryService.getByIdAsync(subsidiaryId);
    if (subsidiary.owner?.id !== user.uid) {
      throw new Error('Somente o gerente responsável pela franquia pode salvar esta configuração');
    }
  };

  const persistFinancialConfig = async (acceptedAtMoment: Date | null) => {
    const payload: FinancialConfig[] = configs.map(({ localId, ...config }) => {
      const isActive = !config.disabledAt;
      const nextAcceptedAt = acceptedAtMoment && isActive
        ? acceptedAtMoment
        : (config.acceptedAt ?? null);

      return {
        bank: config.bank,
        services: config.services ?? [],
        account: config.account,
        pixKey: config.pixKey,
        cpfCnpj: removeMask(config.cpfCnpj) ?? '',
        disabledAt: config.disabledAt ?? null,
        acceptedAt: nextAcceptedAt,
        tax: config.bank === Banks.Efibank ? 0.99 : (config.tax ?? 0)
      };
    });

    await subsidiaryService.updateFinancialConfigAsync(payload, subsidiaryId);

    const emailContext = {
      subsidiaryName: subsidiaryName || 'Franquia',
      changedByName: user?.name || user?.displayName || 'Usuário',
      changedByEmail: user?.email || '',
      configs: payload
    };

    if (hasFinancialConfigChanged(configs, savedConfigs)) {
      try {
        await messageService.notifyFinancialConfigChangedAsync(subsidiaryEmail, emailContext);
      } catch (emailError: any) {
        enqueueSnackbar(
          emailError?.message ?? 'Configuração salva, mas o e-mail de notificação não foi enviado',
          { variant: 'warning' }
        );
      }
    }

    if (acceptedAtMoment) {
      try {
        await messageService.notifyFinancialTermsAcceptedAsync(subsidiaryEmail, {
          subsidiaryName: emailContext.subsidiaryName,
          acceptedByName: emailContext.changedByName,
          acceptedByEmail: emailContext.changedByEmail,
          acceptedAt: acceptedAtMoment,
          configs: payload
        });
      } catch (emailError: any) {
        enqueueSnackbar(
          emailError?.message ?? 'Configuração salva, mas o e-mail do termo de aceite não foi enviado',
          { variant: 'warning' }
        );
      }
    }

    setConfigs((previous) => previous.map((item, index) => ({
      ...item,
      acceptedAt: payload[index]?.acceptedAt ?? item.acceptedAt
    })));
    setSavedConfigs(payload);
  };

  const closeTermsDialog = () => {
    if (isSaving) return;
    setTermsDialogOpen(false);
    clearTermsAcceptance();
  };

  const handleConfirmTerms = () => {
    if (!termsAccepted) return;
    const acceptedAt = new Date();
    termsAcceptedAtRef.current = acceptedAt;
    setTermsAcceptedAt(acceptedAt);
    setTermsDialogOpen(false);
    setPassword('');
    setPasswordError('');
    setPasswordDialogOpen(true);
  };

  const closePasswordDialog = () => {
    if (isSaving) return;
    setPasswordDialogOpen(false);
    setPassword('');
    setPasswordError('');
    clearTermsAcceptance();
  };

  const handleConfirmPasswordSave = async () => {
    if (!password.trim()) {
      setPasswordError('Informe sua senha para confirmar');
      return;
    }

    setIsSaving(true);
    setPasswordError('');
    try {
      await assertFranchiseManagerPermission();

      const { currentUser } = getAuth();
      if (!currentUser?.email) {
        throw new Error('Usuário não autenticado');
      }

      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);
      await persistFinancialConfig(termsAcceptedAtRef.current ?? termsAcceptedAt);

      setSuccess(true);
      setPasswordDialogOpen(false);
      setPassword('');
      clearTermsAcceptance();
      enqueueSnackbar('Configurações financeiras salvas com sucesso', { variant: 'success' });
    } catch (e: any) {
      const message = getPasswordErrorMessage(e);
      if (e?.code?.startsWith?.('auth/')) {
        setPasswordError(message);
      } else {
        enqueueSnackbar(message, { variant: 'error' });
        setPasswordDialogOpen(false);
        setPassword('');
        clearTermsAcceptance();
      }
      setSuccess(false);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Red+Hat+Display:wght@500;700&display=swap';
    document.head.appendChild(fontLink);

    return () => {
      document.head.removeChild(fontLink);
    };
  }, []);

  useEffect(() => {
    subsidiaryService.getByIdAsync(subsidiaryId).then((item) => {
      const financialConfig = item.settings.financialConfig ?? [];
      setConfigs(financialConfig.map(toFinancialConfigItem));
      setSavedConfigs(financialConfig.map((config) => ({
        bank: config.bank ?? '',
        services: config.services ?? [],
        account: config.account ?? '',
        pixKey: config.pixKey ?? '',
        cpfCnpj: removeMask(config.cpfCnpj) ?? '',
        disabledAt: parseDateValue(config.disabledAt),
        acceptedAt: parseDateValue(config.acceptedAt),
        tax: config.tax ?? 0
      })));
      setSubsidiaryEmail(item.email ?? '');
      setSubsidiaryName(item.description ?? '');
      if (financialConfig.length > 0) {
        setSelectedIndex(0);
      }
    }).catch(() => {
      enqueueSnackbar('Erro ao carregar configurações financeiras', { variant: 'error' });
    }).finally(() => {
      setIsLoading(false);
    });
  }, [subsidiaryId, enqueueSnackbar]);

  const handleSelect = (index: number) => {
    setSelectedIndex(index);
    setSuccess(false);
  };

  const handleAdd = () => {
    const newConfigs = [...configs, emptyConfig()];
    setConfigs(newConfigs);
    setSelectedIndex(newConfigs.length - 1);
    setSuccess(false);
  };

  const handleRemove = (index: number) => {
    const newConfigs = configs.filter((_, i) => i !== index);
    setConfigs(newConfigs);

    if (selectedIndex === null) return;

    if (newConfigs.length === 0) {
      setSelectedIndex(null);
    } else if (selectedIndex >= newConfigs.length) {
      setSelectedIndex(newConfigs.length - 1);
    } else if (selectedIndex === index) {
      setSelectedIndex(Math.min(index, newConfigs.length - 1));
    }
    setSuccess(false);
  };

  const handleFieldChange = (
    field: keyof FinancialConfig,
    value: string | string[] | Date | number | null
  ) => {
    if (selectedIndex === null) return;

    const newConfigs = [...configs];
    const updatedConfig = { ...newConfigs[selectedIndex], [field]: value };

    if (field === 'bank' && value === Banks.Efibank) {
      updatedConfig.tax = 0.99;
    }

    newConfigs[selectedIndex] = updatedConfig;
    setConfigs(newConfigs);
    setSuccess(false);

    if (field === 'cpfCnpj' && typeof value === 'string') {
      const { localId } = newConfigs[selectedIndex];
      const error = value.trim() ? getCpfCnpjError(value) : '';
      setCpfCnpjErrors((prev) => ({ ...prev, [localId]: error }));
    }
  };

  const handleCpfCnpjBlur = () => {
    if (!selectedConfig) return;

    const error = getCpfCnpjError(selectedConfig.cpfCnpj);
    setCpfCnpjErrors((prev) => ({ ...prev, [selectedConfig.localId]: error }));
  };

  const handleSave = async () => {
    if (!canSaveAsManager) {
      enqueueSnackbar(
        'Somente o gerente da franquia pode salvar esta configuração',
        { variant: 'warning' }
      );
      return;
    }

    const errors: Record<string, string> = {};
    configs.forEach((config) => {
      const error = getCpfCnpjError(config.cpfCnpj);
      if (error) errors[config.localId] = error;
    });
    setCpfCnpjErrors(errors);

    const hasEmptyFields = configs.some(
      (config) => !config.bank.trim()
        || !config.services?.length
        || !config.account.trim()
        || !config.pixKey.trim()
        || !config.cpfCnpj.trim()
    );

    if (hasEmptyFields) {
      enqueueSnackbar('Preencha todos os campos antes de salvar', { variant: 'warning' });
      return;
    }

    if (Object.keys(errors).length > 0) {
      enqueueSnackbar('Corrija os CPF/CNPJ inválidos antes de salvar', { variant: 'warning' });
      return;
    }

    const duplicate = findDuplicateFinancialConfig(configs);
    if (duplicate) {
      setSelectedIndex(duplicate.index);
      enqueueSnackbar(duplicate.message, { variant: 'warning' });
      return;
    }

    const hasActiveConfig = configs.some((config) => !config.disabledAt);
    if (hasActiveConfig) {
      clearTermsAcceptance();
      setTermsDialogOpen(true);
      return;
    }

    setPassword('');
    setPasswordError('');
    setPasswordDialogOpen(true);
  };

  const getListLabel = (config: FinancialConfigItem, index: number) => {
    if (config.bank.trim()) return BanksList[config.bank as Banks] ?? config.bank;
    return `Configuração ${index + 1}`;
  };

  const getSelectedServiceOptions = (services: string[]) => bankServiceOptions.filter(
    (option) => services.includes(option.value)
  );

  if (isLoading) {
    return (
      <WrapperSimple sectionHeading="Financeira">
        <div className="d-flex align-items-center justify-content-center" style={{ height: '360px' }}>
          <ScaleLoader color="var(--primary)" loading />
        </div>
      </WrapperSimple>
    );
  }

  return (
    <WrapperSimple sectionHeading="Financeira">
      <Dialog
        open={termsDialogOpen}
        onClose={closeTermsDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="terms-acceptance-dialog-title"
      >
        <DialogTitle id="terms-acceptance-dialog-title">
          Termo de Responsabilidade
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div" sx={{ mb: 2 }}>
            <Typography variant="body2" paragraph>
              Antes de gravar ou ativar uma conta bancária ou integração Pix, leia atentamente o termo abaixo.
            </Typography>
            <Box
              sx={{
                maxHeight: 240,
                overflowY: 'auto',
                p: 2,
                mb: 2,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                {FINANCIAL_RESPONSIBILITY_TERMS.title}
              </Typography>
              <Typography variant="body2" paragraph>
                {FINANCIAL_RESPONSIBILITY_TERMS.intro}
              </Typography>
              <Typography variant="body2" component="div">
                <ol style={{ margin: 0, paddingLeft: 20 }}>
                  {FINANCIAL_RESPONSIBILITY_TERMS.items.map((item) => (
                    <li key={item.id} style={{ marginBottom: 8 }}>
                      {item.type === 'rich' ? (
                        <>
                          <strong>{item.heading}</strong>
                          <br />
                          {item.subheading ? (
                            <>
                              <strong>{item.subheading}</strong>
                              {' '}
                            </>
                          ) : null}
                          {item.contentBefore}
                          <Link
                            href={item.link.url}
                            target="_blank"
                            rel="noreferrer"
                            underline="hover"
                          >
                            {item.link.label}
                          </Link>
                          {item.contentAfter}
                        </>
                      ) : item.content}
                    </li>
                  ))}
                </ol>
              </Typography>
            </Box>
            <FormControlLabel
              sx={{ alignItems: 'flex-start', mr: 0 }}
              control={(
                <Checkbox
                  checked={termsAccepted}
                  onChange={(event) => setTermsAccepted(event.target.checked)}
                  color="primary"
                  sx={{ pt: 0 }}
                />
              )}
              label={FINANCIAL_RESPONSIBILITY_TERMS.checkboxLabel}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTermsDialog}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmTerms}
            variant="contained"
            color="primary"
            disabled={!termsAccepted}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={passwordDialogOpen}
        onClose={closePasswordDialog}
        disableEscapeKeyDown={isSaving}
        aria-labelledby="confirm-password-dialog-title"
      >
        <DialogTitle id="confirm-password-dialog-title">
          Confirmar alteração
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Por segurança, digite a senha do gerente da franquia para salvar as configurações financeiras.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            required
            type="password"
            label="Senha"
            variant="outlined"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) setPasswordError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleConfirmPasswordSave();
              }
            }}
            error={Boolean(passwordError)}
            helperText={passwordError}
            disabled={isSaving}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePasswordDialog} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmPasswordSave}
            variant="contained"
            color="primary"
            disabled={isSaving}
          >
            {isSaving ? 'Confirmando...' : 'Confirmar e salvar'}
          </Button>
        </DialogActions>
      </Dialog>
      <Grid container spacing={0} sx={{ minHeight: 400 }}>
        <Grid item xs={12} md={6} sx={{ borderRight: { md: '1px solid #e0e0e0' }, pr: { md: 2 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              Configurações Pix/Conta Bancária
            </Typography>
            <IconButton color="primary" onClick={handleAdd} size="small" title="Adicionar configuração">
              <AddIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 1 }} />
          {configs.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Nenhuma configuração cadastrada.
              <br />
              Clique em + para adicionar.
            </Typography>
          ) : (
            <List disablePadding>
              {configs.map((config, index) => (
                <ListItemButton
                  key={config.localId}
                  selected={selectedIndex === index}
                  onClick={() => handleSelect(index)}
                  divider
                >
                  <ListItemText
                    primary={getListLabel(config, index)}
                    secondary={
                      config.disabledAt
                        ? 'Inativo'
                        : (config.account.trim() || 'Conta não informada')
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Grid>

        <Grid item xs={12} md={6} sx={{ pl: { md: 2 }, pt: { xs: 3, md: 0 } }}>
          {selectedConfig ? (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Detalhes
                </Typography>
                <IconButton
                  color="error"
                  onClick={() => handleRemove(selectedIndex!)}
                  size="small"
                  title="Remover configuração"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl variant="outlined" fullWidth required>
                    <InputLabel>Banco</InputLabel>
                    <Select
                      label="Banco"
                      value={selectedConfig.bank}
                      onChange={(e) => handleFieldChange('bank', e.target.value)}
                    >
                      {Object.entries(BanksList).map(([key, label]) => (
                        <MenuItem key={key} value={key}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={bankServiceOptions}
                    value={getSelectedServiceOptions(selectedConfig.services)}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    disableCloseOnSelect
                    onChange={(_, value) => {
                      handleFieldChange('services', value.map((item) => item.value));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tipo de serviço"
                        variant="outlined"
                        placeholder="Selecione..."
                        fullWidth
                        required
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    label="CPF ou CNPJ"
                    value={selectedConfig.cpfCnpj}
                    onChange={(e) => handleFieldChange('cpfCnpj', e.target.value)}
                    onBlur={handleCpfCnpjBlur}
                    error={Boolean(selectedCpfCnpjError)}
                    helperText={selectedCpfCnpjError}
                    InputProps={{ inputComponent: CpfCnpjMask as any }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    label="Conta com dígito"
                    value={selectedConfig.account}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    onChange={(e) => handleFieldChange('account', removeMask(e.target.value) ?? '')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    label="Chave Pix"
                    value={selectedConfig.pixKey}
                    onChange={(e) => handleFieldChange('pixKey', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={!selectedConfig.disabledAt}
                        onChange={(event) => {
                          handleFieldChange('disabledAt', event.target.checked ? null : new Date());
                        }}
                        name="disabledAt"
                      />
                    )}
                    label="Ativo"
                  />
                </Grid>
              </Grid>
              {isEfipaySelected && (
                <Box
                  mt={3}
                  pt={2}
                  px={2}
                  pb={2}
                  borderRadius={1}
                  sx={{
                    background: EFI_THEME.background,
                    borderLeft: `3px solid ${EFI_THEME.accent}`,
                    borderTop: '1px solid rgba(11, 161, 194, 0.2)'
                  }}
                >
                  <Link
                    href={EFI_PARTNER_URL}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                    sx={{
                      display: 'inline-block',
                      fontFamily: EFI_THEME.fontFamily,
                      fontSize: '0.95rem',
                      fontWeight: 700,
                      color: EFI_THEME.primary,
                      letterSpacing: '0.3px',
                      textDecorationColor: EFI_THEME.primaryLight,
                      transition: 'color 0.3s cubic-bezier(0.4, 0, 0.2, 1), text-decoration-color 0.3s',
                      '&:hover': {
                        color: EFI_THEME.primaryLight
                      },
                      '&:active': {
                        color: EFI_THEME.primaryDark
                      }
                    }}
                  >
                    Parceria Efi Bank + Juma Delivery
                  </Link>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1.5,
                      fontFamily: EFI_THEME.fontFamily,
                      color: '#454d58',
                      letterSpacing: '0.3px'
                    }}
                  >
                    Abra sua conta com tarifa reduzida use o
                    {' '}
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 700,
                        color: EFI_THEME.primaryDark,
                        letterSpacing: '0.5px'
                      }}
                    >
                      cupom: JUM4EN7R
                    </Box>
                    {' '}
                    <Link href="https://sejaefi.com.br/abrir-conta" target="_blank" rel="noreferrer">
                      Clique aqui
                    </Link>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1,
                      fontFamily: EFI_THEME.fontFamily,
                      fontWeight: 700,
                      color: EFI_THEME.primaryDark,
                      letterSpacing: '0.3px',
                      backgroundColor: 'rgba(11, 161, 194, 0.12)',
                      borderLeft: `4px solid ${EFI_THEME.accent}`
                    }}
                  >
                    Hoje a taxa PIX é R$ 0,80 por transação.
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1.5,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1,
                      fontFamily: EFI_THEME.fontFamily,
                      fontWeight: 700,
                      color: EFI_THEME.primaryDark,
                      letterSpacing: '0.3px',
                      backgroundColor: 'rgba(11, 161, 194, 0.12)',
                      borderLeft: `4px solid ${EFI_THEME.accent}`
                    }}
                  >
                    Atenção: a conta informada deve ser obrigatoriamente da Efibank.
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              minHeight={200}
            >
              <Typography color="text.secondary">
                Selecione uma configuração ou adicione uma nova.
              </Typography>
            </Box>
          )}
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <div className={classes.wrapper}>
              <Fab
                aria-label="save"
                color="primary"
                className={buttonClassname}
                onClick={handleSave}
                disabled={isSaving || !canSaveAsManager}
              >
                {success ? <CheckIcon /> : <SaveIcon />}
              </Fab>
              {isSaving && (
                <CircularProgress size={68} className={classes.fabProgress} />
              )}
            </div>
          </Box>
        </Grid>
      </Grid>
    </WrapperSimple>
  );
}
